import { RefreshCw, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { useWrapRequest } from 'use-wrap-request';
import { PageHeader } from '@components/page-header';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { toast } from '@core/toast';
import { ConfirmationModal } from '../../components/confirmation-modal';
import { CellProps, TableComponent } from '../../components/table';
import { Api } from '../../core/api';
import { PurchaseEventDlqSupportDto } from '../../core/api/WalletClient';
import { links } from '../../core/router';
import { injectTSDI } from '../../core/tsdi';

type DlqRow = PurchaseEventDlqSupportDto & { id: number };

type ConfirmActionType = 'retry' | 'purge';

interface ConfirmActionState {
    action: ConfirmActionType;
    purchaseId: number;
    purchaseHasOrder: boolean;
}

const DEFAULT_LIMIT = 100;
const LIMIT_OPTIONS = [50, 100, 200, 500, 1000];

function getErrorMessage(error: any): string {
    if (error && typeof error === 'object' && 'error' in error) {
        const errorObject = error.error;
        if (errorObject?.message?.defaultMessage) {
            return errorObject.message.defaultMessage;
        }
        if (errorObject?.defaultMessage) {
            return errorObject.defaultMessage;
        }
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'Unexpected error';
}

const PurchaseEventDlq = observer(() => {
    const { walletClient } = injectTSDI(Api);

    const [limit, setLimit] = React.useState(DEFAULT_LIMIT);
    const [purchaseIdFilter, setPurchaseIdFilter] = React.useState<
        number | undefined
    >(undefined);
    const [retryingId, setRetryingId] = React.useState<number | null>(null);
    const [purgingId, setPurgingId] = React.useState<number | null>(null);
    const [confirmAction, setConfirmAction] =
        React.useState<ConfirmActionState | null>(null);

    const messages = useWrapRequest(
        async (limit: number, purchaseId?: number) => {
            const response =
                await walletClient.supportApi.purchaseEventDlqSupportControllerGetMessages(
                    {
                        limit,
                        ...(purchaseId ? { purchaseId } : {})
                    }
                );

            // DLQ messages have no stable id and may share (or lack) a purchaseId, so use the index.
            const content: DlqRow[] = (response.data.content || []).map(
                (message, index) => ({ ...message, id: index })
            );

            return {
                content,
                number: 0,
                size: limit,
                totalElements: content.length
            };
        },
        {
            defaultData: {
                content: [],
                number: 0,
                size: DEFAULT_LIMIT,
                totalElements: 0
            }
        }
    );

    React.useEffect(() => {
        messages.request([limit, purchaseIdFilter]);
    }, [limit, purchaseIdFilter]);

    const reload = () => {
        messages.request([limit, purchaseIdFilter]);
    };

    const handleSearch = (value?: string) => {
        const parsed = Number(value);
        setPurchaseIdFilter(
            value && Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
        );
    };

    const handleConfirmAction = async () => {
        if (!confirmAction) {
            return false;
        }

        const { action, purchaseId } = confirmAction;
        const isRetry = action === 'retry';

        if (isRetry) {
            setRetryingId(purchaseId);
        } else {
            setPurgingId(purchaseId);
        }

        try {
            if (isRetry) {
                await walletClient.supportApi.purchaseEventDlqSupportControllerRetry(
                    purchaseId
                );
            } else {
                await walletClient.supportApi.purchaseEventDlqSupportControllerPurge(
                    purchaseId
                );
            }
            toast.success(
                isRetry
                    ? 'DLQ message moved back to the queue'
                    : 'DLQ message purged'
            );
            setConfirmAction(null);
            reload();
            return true;
        } catch (e: any) {
            toast.error(getErrorMessage(e));
            return false;
        } finally {
            setRetryingId(null);
            setPurgingId(null);
        }
    };

    const isRowActionLoading = (purchaseId: number) =>
        retryingId === purchaseId || purgingId === purchaseId;

    return (
        <>
            <PageHeader
                title="Purchase Event DLQ"
                badges={
                    <Badge variant="muted">
                        {messages.$?.totalElements ?? 0} shown
                    </Badge>
                }
                actions={
                    <Button variant="outline" onClick={() => reload()}>
                        <RefreshCw />
                        Refresh
                    </Button>
                }
            />

            <Alert variant="info" className="mb-4">
                <AlertDescription>
                    Failed purchase events from{' '}
                    <code>mr.purchase-events.ms-wallet-api.dlq</code>. Retry
                    moves a message back onto the main queue for reprocessing;
                    Purge removes it without reprocessing. Browsing shows up to
                    the selected number of messages.
                </AlertDescription>
            </Alert>

            <TableComponent<DlqRow[]>
                emptyText="No messages on the DLQ"
                searchPlaceholder="Search by purchase ID"
                items={messages}
                rowsPerPageOptions={LIMIT_OPTIONS}
                renderRowSubComponent={({ row }: { row: DlqRow }) => (
                    <div className="p-4">
                        <span className="text-xs text-muted-foreground">
                            Stack trace
                        </span>
                        <pre className="mt-1 max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs break-words whitespace-pre-wrap">
                            {row.errorStackTrace ||
                                row.errorMessage ||
                                'No stack trace available'}
                        </pre>
                    </div>
                )}
                columns={[
                    {
                        Header: 'Purchase ID',
                        id: 'purchaseId',
                        accessor: (row) => row.purchaseId ?? '-',
                        style: { width: 110 }
                    },
                    {
                        Header: 'User ID',
                        id: 'userId',
                        Cell: ({ row: { original } }: CellProps<DlqRow>) =>
                            original.userId ? (
                                <Link to={links.userWallet(original.userId)}>
                                    {original.userId}
                                </Link>
                            ) : (
                                <span className="text-xs text-muted-foreground">
                                    -
                                </span>
                            ),
                        style: { width: 130 }
                    },
                    {
                        Header: 'Source',
                        id: 'vendorSource',
                        accessor: (row) => row.vendorSource ?? '-',
                        style: { width: 120 }
                    },
                    {
                        Header: 'Variant',
                        id: 'productVariant',
                        accessor: (row) => row.productVariant ?? '-'
                    },
                    {
                        Header: 'Order',
                        id: 'purchaseHasOrder',
                        Cell: ({ row: { original } }: CellProps<DlqRow>) =>
                            original.purchaseHasOrder ? (
                                <Badge variant="warning">Has order</Badge>
                            ) : (
                                <Badge variant="muted">No order</Badge>
                            ),
                        style: { width: 120 }
                    },
                    {
                        Header: 'Error',
                        id: 'errorMessage',
                        Cell: ({ row: { original } }: CellProps<DlqRow>) => (
                            <p className="truncate text-sm text-destructive">
                                {original.errorMessage || '-'}
                            </p>
                        )
                    },
                    {
                        Header: 'Deaths',
                        id: 'deathCount',
                        accessor: (row) => row.deathCount ?? '-',
                        style: { width: 90 }
                    },
                    {
                        Header: 'Actions',
                        id: 'actions',
                        Cell: ({ row: { original } }: CellProps<DlqRow>) => {
                            const purchaseId = original.purchaseId;

                            if (typeof purchaseId !== 'number') {
                                return (
                                    <span className="text-xs text-muted-foreground">
                                        -
                                    </span>
                                );
                            }

                            return (
                                <div className="flex gap-1">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-warning"
                                        loading={retryingId === purchaseId}
                                        disabled={isRowActionLoading(
                                            purchaseId
                                        )}
                                        onClick={() =>
                                            setConfirmAction({
                                                action: 'retry',
                                                purchaseId,
                                                purchaseHasOrder: Boolean(
                                                    original.purchaseHasOrder
                                                )
                                            })
                                        }
                                    >
                                        <RefreshCw />
                                        Retry
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-destructive"
                                        loading={purgingId === purchaseId}
                                        disabled={isRowActionLoading(
                                            purchaseId
                                        )}
                                        onClick={() =>
                                            setConfirmAction({
                                                action: 'purge',
                                                purchaseId,
                                                purchaseHasOrder: Boolean(
                                                    original.purchaseHasOrder
                                                )
                                            })
                                        }
                                    >
                                        <Trash2 />
                                        Purge
                                    </Button>
                                </div>
                            );
                        },
                        style: { width: 220 }
                    }
                ]}
                onPageSizeChange={(size) => setLimit(size)}
                onSearchChange={handleSearch}
            />

            <ConfirmationModal
                open={Boolean(confirmAction)}
                title={
                    confirmAction?.action === 'purge'
                        ? 'Confirm Purge'
                        : 'Confirm Retry'
                }
                buttonText={
                    confirmAction?.action === 'purge' ? 'Purge' : 'Retry'
                }
                destructive={confirmAction?.action === 'purge'}
                submitHandler={handleConfirmAction}
                onModalClose={() => setConfirmAction(null)}
            >
                {confirmAction?.action === 'purge' ? (
                    <>
                        <Alert variant="warning">
                            <AlertDescription>
                                This permanently removes the message from the
                                DLQ without reprocessing.
                            </AlertDescription>
                        </Alert>
                        <p className="text-sm">
                            The failed purchase event will not be retried. Use
                            only for messages that will never succeed.
                        </p>
                    </>
                ) : (
                    <>
                        {confirmAction?.purchaseHasOrder && (
                            <Alert variant="warning">
                                <AlertDescription>
                                    This purchase already has an order —
                                    retrying may create a duplicate order
                                    (double charge).
                                </AlertDescription>
                            </Alert>
                        )}
                        <p className="text-sm">
                            This moves the message back onto the main queue and
                            re-attempts order creation at the third-party
                            vendor. No money is refunded to the user.
                        </p>
                    </>
                )}
            </ConfirmationModal>
        </>
    );
});

export default PurchaseEventDlq;
